"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, LoaderCircle, LocateFixed, LogOut, MapPin } from "lucide-react";
import SafeAvatar from "@/components/SafeAvatar";
import { AUTH_UI_RESET_EVENT, useAuth } from "@/components/AuthProvider";
import LogoutButton from "@/components/LogoutButton";
import { useModal } from "@/components/modals/ModalProvider";
import MobileSidebarDrawer from "@/components/nav/MobileSidebarDrawer";
import CartNavActionClient from "@/components/nav/CartNavActionClient";
import AccountMenuItems from "@/components/nav/AccountMenuItems";
import AccountSidebar from "@/components/nav/AccountSidebar";
import { useSupportUnread } from "@/components/support/SupportUnreadProvider";
import YourBarrioLogo from "@/components/brand/YourBarrioLogo";
import { fetchUnreadTotal } from "@/lib/messages";
import { requestJson } from "@/lib/clientRequest";
import { getBusinessDisplayName } from "@/lib/auth/displayName";
import { resolveAvatarUrl } from "@/lib/avatarUrl";
import { getSupabaseBrowserClient } from "@/lib/supabase/browser";
import { useLocation } from "@/components/location/LocationProvider";
import { useCurrentLocationAction } from "@/components/location/useCurrentLocationAction";
import { getLocationLabel, isZipLike, normalizeSelectedLocation } from "@/lib/location";
import {
  buildLocationSuggestionPath,
  LOCATION_SUGGESTION_LIMIT,
  LOCATION_SUGGESTION_MIN_QUERY_LENGTH,
} from "@/lib/location/suggestions";

const UNREAD_REFRESH_EVENT = "yb-unread-refresh";
// Keep resolved placeholder paths aligned so SafeAvatar can switch to initials.
const CUSTOMER_AVATAR_FALLBACK = "/customer-placeholder.png";
const unreadLoadFlights = new Map();

function loadSharedUnreadCount(key, loader) {
  const existing = unreadLoadFlights.get(key);
  if (existing) return existing;
  const pending = Promise.resolve()
    .then(loader)
    .finally(() => {
      if (unreadLoadFlights.get(key) === pending) unreadLoadFlights.delete(key);
    });
  unreadLoadFlights.set(key, pending);
  return pending;
}

function parseProfileUpdatedAt(profile) {
  const timestamp = Date.parse(profile?.updated_at || "");
  return Number.isFinite(timestamp) ? timestamp : null;
}

function hasProfileValue(value) {
  return String(value || "").trim().length > 0;
}

function chooseLiveAccountProfile({
  forcedProfile,
  contextProfile,
  forcedUser,
  contextUser,
}) {
  if (!forcedProfile) return contextProfile ?? null;
  if (!contextProfile) return forcedProfile;

  const forcedUserId = forcedUser?.id || forcedProfile?.id || null;
  const contextUserId = contextUser?.id || contextProfile?.id || null;
  if (forcedUserId && contextUserId && forcedUserId !== contextUserId) {
    return forcedProfile;
  }

  const forcedUpdatedAt = parseProfileUpdatedAt(forcedProfile);
  const contextUpdatedAt = parseProfileUpdatedAt(contextProfile);
  if (
    contextUpdatedAt !== null &&
    (forcedUpdatedAt === null || contextUpdatedAt >= forcedUpdatedAt)
  ) {
    return {
      ...forcedProfile,
      ...contextProfile,
    };
  }

  if (forcedUpdatedAt !== null && contextUpdatedAt !== null && forcedUpdatedAt > contextUpdatedAt) {
    return forcedProfile;
  }

  const mergedProfile = {
    ...forcedProfile,
    ...contextProfile,
  };

  for (const key of [
    "full_name",
    "phone",
    "address",
    "address_2",
    "city",
    "state",
    "postal_code",
    "email",
    "role",
  ]) {
    if (!hasProfileValue(contextProfile?.[key]) && hasProfileValue(forcedProfile?.[key])) {
      mergedProfile[key] = forcedProfile[key];
    }
  }

  return mergedProfile;
}

export default function HeaderAccountWidget({
  surface = "public",
  variant = "desktop",
  forcedAuth = null,
  mobileMenuOpen = false,
  onCloseMobileMenu = null,
  mobileDrawerId = undefined,
}) {
  const router = useRouter();
  const {
    supabase,
    user,
    profile,
    business,
    role,
    authStatus,
    rateLimited,
    rateLimitMessage,
    authBusy,
    authAction,
    authAttemptId,
    lastAuthEvent,
    providerInstanceId,
    hasBusinessAccess: contextHasBusinessAccess,
    businessAccessContexts: contextBusinessAccessContexts,
  } = useAuth();
  const { openModal } = useModal();
  const { location, setLocation, requestGpsLocation } = useLocation();
  const authDiagEnabled =
    process.env.NEXT_PUBLIC_AUTH_DIAG === "1";
  const loading = authStatus === "loading";
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [accountSidebarOpen, setAccountSidebarOpen] = useState(false);
  const [hasAdminMembership, setHasAdminMembership] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { count: supportUnreadCount } = useSupportUnread();
  const [hasHydrated, setHasHydrated] = useState(false);
  const [mobileLocationOpen, setMobileLocationOpen] = useState(false);
  const [mobileLocationInput, setMobileLocationInput] = useState("");
  const [mobileLocationQueryEdited, setMobileLocationQueryEdited] = useState(false);
  const [mobileLocationSuggestions, setMobileLocationSuggestions] = useState([]);
  const [mobileLocationSuggestLoading, setMobileLocationSuggestLoading] = useState(false);
  const [mobileLocationSuggestError, setMobileLocationSuggestError] = useState(null);
  const [mobileLocationSuggestIndex, setMobileLocationSuggestIndex] = useState(-1);
  const [mobileLocationSelectHint, setMobileLocationSelectHint] = useState(null);
  // Location display derives only from the global provider state.
  const locationLabel = getLocationLabel(location);
  const mobileLocationNoMatchMessage = isZipLike(mobileLocationInput)
    ? "No matches. Try another postal code."
    : "No matches. Try another city.";
  const dropdownRef = useRef(null);
  const accountTriggerRef = useRef(null);
  const lastUnreadKeyRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const unreadRequestIdRef = useRef(0);
  const mobileLocationPrefillRef = useRef(false);
  const mobileLocationSuggestAbortRef = useRef(null);
  const mobileLocationSuggestReqIdRef = useRef(0);
  const mobileLocationSuggestSpinnerRef = useRef(null);
  const mobileLocationSuggestShownAtRef = useRef(0);

  const handleMobileCurrentLocationSuccess = useCallback(() => {
    router.refresh();
    setMobileLocationInput("");
    setMobileLocationSuggestions([]);
    setMobileLocationSuggestIndex(-1);
    setMobileLocationSelectHint(null);
    setMobileLocationQueryEdited(false);
    setMobileLocationOpen(false);
  }, [router]);
  const {
    currentLocationLoading,
    currentLocationError,
    clearCurrentLocationError,
    requestCurrentLocation,
  } = useCurrentLocationAction({
    requestGpsLocation,
    onSuccess: handleMobileCurrentLocationSuccess,
  });

  const supportModeActive = Boolean(forcedAuth?.supportMode);
  const forcedAuthUser = forcedAuth?.user?.id ? forcedAuth.user : null;
  const contextAuthUser = user?.id ? user : null;
  const accountUser = forcedAuthUser || contextAuthUser;
  const chosenUserSource = forcedAuthUser ? "forced" : contextAuthUser ? "context" : "none";
  const accountProfile = useMemo(
    () =>
      chooseLiveAccountProfile({
        forcedProfile: forcedAuth?.profile,
        contextProfile: profile,
        forcedUser: forcedAuthUser,
        contextUser: contextAuthUser,
      }),
    [contextAuthUser, forcedAuth?.profile, forcedAuthUser, profile]
  );
  const accountBusiness = forcedAuth?.business || business;
  const effectiveRole = forcedAuth?.role || role;
  const isCustomer = effectiveRole === "customer";
  const isBusiness = effectiveRole === "business";
  useEffect(() => {
    const menuOpen = profileMenuOpen || accountSidebarOpen || mobileMenuOpen;
    if (!menuOpen || !isCustomer || !accountUser?.id || supportModeActive) {
      setHasAdminMembership(false);
      return;
    }
    let active = true;
    const checkMembership = async () => {
      try {
        const response = await fetch("/api/account/admin-membership", { cache: "no-store" });
        const payload = response.ok ? await response.json() : null;
        if (active) setHasAdminMembership(payload?.hasAdminMembership === true);
      } catch {
        if (active) setHasAdminMembership(false);
      }
    };
    setHasAdminMembership(false);
    void checkMembership();
    const interval = window.setInterval(checkMembership, 15000);
    return () => { active = false; window.clearInterval(interval); };
  }, [profileMenuOpen, accountSidebarOpen, mobileMenuOpen, isCustomer, accountUser?.id, supportModeActive]);
  const hasBusinessAccess =
    isBusiness ||
    forcedAuth?.accountContext?.hasBusinessAccess === true ||
    contextHasBusinessAccess === true;
  const businessAccessContexts =
    forcedAuth?.accountContext?.businessAccessContexts || contextBusinessAccessContexts || [];
  const sidebarFeatureEnabled = process.env.NEXT_PUBLIC_ACCOUNT_SIDEBAR !== "0";
  const [useSidebarDesktop, setUseSidebarDesktop] = useState(sidebarFeatureEnabled);

  const avatar = resolveAvatarUrl(
    accountProfile?.profile_photo_url,
    accountUser?.user_metadata,
    profile?.profile_photo_url,
    user?.user_metadata
  );

  const displayName = isBusiness
    ? getBusinessDisplayName({
        business: accountBusiness,
        profile: accountProfile,
        user: accountUser,
      })
    : accountProfile?.full_name ||
      accountUser?.user_metadata?.full_name ||
      accountUser?.user_metadata?.name ||
      accountUser?.email ||
      "Account";

  const email = accountProfile?.email || accountUser?.email || null;
  // Guest/account rendering must depend only on authenticated user presence.
  // Profile, metadata, and avatar readiness are display details and must never
  // downgrade an authenticated user to the public CTA branch.
  const hasAuth = Boolean(accountUser?.id);
  const isMobileBusinessAccount = hasAuth && isBusiness;
  const disableCtas = authBusy || loading;
  const showRateLimit = rateLimited && hasAuth;
  const branchChosen = hasAuth ? "account" : loading ? "loading" : "guest";

  useEffect(() => {
    if (!authDiagEnabled) return;
    const payload = {
      surface,
      variant,
      authStatus,
      hasForcedAuthUser: Boolean(forcedAuthUser?.id),
      hasContextUser: Boolean(contextAuthUser?.id),
      chosenUserSource,
      chosenHasUser: hasAuth,
      chosenUserId: accountUser?.id || null,
      hasProfile: Boolean(accountProfile?.id),
      resolvedAvatarSrc: avatar || null,
      branchChosen,
    };
    console.info("[AUTH_HEADER_RENDER]", payload);
    if (branchChosen === "guest" && (forcedAuthUser?.id || contextAuthUser?.id)) {
      console.warn("[AUTH_HEADER_RENDER] guest_selected_with_auth_user", payload);
    }
  }, [
    accountProfile?.id,
    accountUser?.id,
    authDiagEnabled,
    authStatus,
    avatar,
    branchChosen,
    chosenUserSource,
    contextAuthUser?.id,
    forcedAuthUser?.id,
    hasAuth,
    surface,
    variant,
  ]);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const unreadUserId = accountUser?.id || accountProfile?.id;
  const canLoadUnread =
    Boolean(unreadUserId) &&
    isCustomer &&
    (authStatus === "authenticated" || (authStatus === "loading" && hasAuth));
  const loadUnreadCount = useCallback(async () => {
    const activeClient = supabase ?? getSupabaseBrowserClient();
    if (!canLoadUnread) {
      return;
    }
    const requestId = ++unreadRequestIdRef.current;
    try {
      const total = await loadSharedUnreadCount(`customer:${unreadUserId}`, async () => {
        try {
          const { payload } = await requestJson(
            "/api/customer/conversations?unreadOnly=1",
            {
              method: "GET",
              credentials: "include",
              cache: "no-store",
              timeoutMs: 5000,
              retries: 1,
            }
          );
          const value = Number(payload?.unreadTotal);
          if (!Number.isFinite(value)) throw new Error("invalid_unread_total");
          return value;
        } catch {
          if (!activeClient) throw new Error("missing_supabase_client");
          return fetchUnreadTotal({
            supabase: activeClient,
            userId: unreadUserId,
            role: "customer",
          });
        }
      });

      if (requestId !== unreadRequestIdRef.current) return;
      setUnreadCount(total);
    } catch {
      // best effort
    }
  }, [supabase, canLoadUnread, unreadUserId]);

  useEffect(() => {
    if (canLoadUnread) return;
    unreadRequestIdRef.current += 1;
    lastUnreadKeyRef.current = null;
    setUnreadCount(0);
  }, [canLoadUnread, unreadUserId, effectiveRole]);

  const scheduleUnreadRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(() => {
      void loadUnreadCount();
    }, 0);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!hasAuth || !isCustomer || !unreadUserId || authStatus === "unauthenticated") {
      return undefined;
    }
    const key = `${unreadUserId}:${authStatus}`;
    if (lastUnreadKeyRef.current === key) return undefined;
    lastUnreadKeyRef.current = key;
    scheduleUnreadRefresh();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        scheduleUnreadRefresh();
      }
    };
    window.addEventListener("focus", scheduleUnreadRefresh);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", scheduleUnreadRefresh);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [hasAuth, isCustomer, scheduleUnreadRefresh, unreadUserId, authStatus]);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) return;
    const timeoutId = setTimeout(() => {
      setMobileLocationOpen(false);
      setMobileLocationInput("");
      setMobileLocationSuggestions([]);
      setMobileLocationSuggestIndex(-1);
      setMobileLocationSelectHint(null);
      setMobileLocationSuggestError(null);
      setMobileLocationSuggestLoading(false);
      setMobileLocationQueryEdited(false);
      mobileLocationPrefillRef.current = false;
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [mobileMenuOpen]);

  useEffect(() => {
    let timeoutId = null;
    if (!mobileLocationOpen) {
      mobileLocationPrefillRef.current = false;
      timeoutId = setTimeout(() => setMobileLocationQueryEdited(false), 0);
      return () => clearTimeout(timeoutId);
    }
    if (mobileLocationPrefillRef.current) return;
    mobileLocationPrefillRef.current = true;
    timeoutId = setTimeout(() => {
      setMobileLocationInput(locationLabel !== "Set location" ? locationLabel : "");
      setMobileLocationQueryEdited(false);
      setMobileLocationSuggestions([]);
      setMobileLocationSuggestIndex(-1);
      setMobileLocationSelectHint(null);
    }, 0);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [mobileLocationOpen, locationLabel]);

  useEffect(() => {
    const scheduled = new Set();
    const schedule = (fn) => {
      const id = setTimeout(fn, 0);
      scheduled.add(id);
    };
    const clearScheduled = () => {
      scheduled.forEach((id) => clearTimeout(id));
      scheduled.clear();
    };
    const abortInflight = () => {
      if (mobileLocationSuggestAbortRef.current) {
        mobileLocationSuggestAbortRef.current.abort();
        mobileLocationSuggestAbortRef.current = null;
      }
    };

    if (mobileLocationSuggestSpinnerRef.current) {
      clearTimeout(mobileLocationSuggestSpinnerRef.current);
      mobileLocationSuggestSpinnerRef.current = null;
    }

    if (!mobileLocationOpen || !mobileLocationQueryEdited) {
      abortInflight();
      mobileLocationSuggestReqIdRef.current += 1;
      mobileLocationSuggestShownAtRef.current = 0;
      schedule(() => setMobileLocationSuggestLoading(false));
      schedule(() => setMobileLocationSuggestError(null));
      schedule(() => setMobileLocationSuggestIndex(-1));
      schedule(() => setMobileLocationSuggestions([]));
      schedule(() => setMobileLocationSelectHint(null));
      return clearScheduled;
    }

    const term = mobileLocationInput.trim();

    if (term.length === 0) {
      abortInflight();
      mobileLocationSuggestReqIdRef.current += 1;
      mobileLocationSuggestShownAtRef.current = 0;
      schedule(() => setMobileLocationSuggestLoading(false));
      schedule(() => setMobileLocationSuggestError(null));
      schedule(() => setMobileLocationSuggestIndex(-1));
      schedule(() => setMobileLocationSuggestions([]));
      schedule(() => setMobileLocationSelectHint(null));
      return clearScheduled;
    }

    if (term.length < LOCATION_SUGGESTION_MIN_QUERY_LENGTH) {
      abortInflight();
      mobileLocationSuggestReqIdRef.current += 1;
      mobileLocationSuggestShownAtRef.current = 0;
      schedule(() => setMobileLocationSuggestLoading(false));
      schedule(() => setMobileLocationSuggestError(null));
      schedule(() => setMobileLocationSuggestIndex(-1));
      schedule(() => setMobileLocationSelectHint(null));
      return clearScheduled;
    }

    const reqId = ++mobileLocationSuggestReqIdRef.current;

    abortInflight();
    const controller = new AbortController();
    mobileLocationSuggestAbortRef.current = controller;

    mobileLocationSuggestSpinnerRef.current = setTimeout(() => {
      if (reqId !== mobileLocationSuggestReqIdRef.current) return;
      mobileLocationSuggestShownAtRef.current = Date.now();
      setMobileLocationSuggestLoading(true);
    }, 150);

    const handle = setTimeout(() => {
      setMobileLocationSuggestError(null);

      const url = buildLocationSuggestionPath(term, location, {
        debug: process.env.NODE_ENV !== "production",
      });

      fetch(url, { signal: controller.signal })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data?.error || "location_suggest_failed");
          }
          return data;
        })
        .then((data) => {
          if (controller.signal.aborted) return;
          if (reqId !== mobileLocationSuggestReqIdRef.current) return;
          const next = Array.isArray(data?.suggestions)
            ? data.suggestions.slice(0, LOCATION_SUGGESTION_LIMIT)
            : [];
          setMobileLocationSuggestions(next);
          setMobileLocationSuggestIndex(-1);
          setMobileLocationSelectHint(null);
          if (data?.error) {
            setMobileLocationSuggestError(data.error);
          }
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (reqId !== mobileLocationSuggestReqIdRef.current) return;
          setMobileLocationSuggestError(err?.message || "Location suggestions unavailable.");
          setMobileLocationSuggestions([]);
          setMobileLocationSuggestIndex(-1);
          setMobileLocationSelectHint(null);
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          if (reqId !== mobileLocationSuggestReqIdRef.current) return;
          if (mobileLocationSuggestSpinnerRef.current) {
            clearTimeout(mobileLocationSuggestSpinnerRef.current);
            mobileLocationSuggestSpinnerRef.current = null;
          }
          const shownAt = mobileLocationSuggestShownAtRef.current || 0;
          const elapsed = shownAt ? Date.now() - shownAt : 9999;
          const remaining = 300 - elapsed;

          if (remaining > 0) {
            setTimeout(() => {
              if (controller.signal.aborted) return;
              if (reqId !== mobileLocationSuggestReqIdRef.current) return;
              setMobileLocationSuggestLoading(false);
            }, remaining);
          } else {
            setMobileLocationSuggestLoading(false);
          }
        });
    }, 250);

    return () => {
      clearScheduled();
      clearTimeout(handle);
      controller.abort();
      if (mobileLocationSuggestSpinnerRef.current) {
        clearTimeout(mobileLocationSuggestSpinnerRef.current);
        mobileLocationSuggestSpinnerRef.current = null;
      }
    };
  }, [location, mobileLocationOpen, mobileLocationInput, mobileLocationQueryEdited]);

  useEffect(() => {
    if (!mobileLocationOpen) clearCurrentLocationError();
  }, [clearCurrentLocationError, mobileLocationOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleUnreadRefresh = () => {
      loadUnreadCount();
    };
    window.addEventListener(UNREAD_REFRESH_EVENT, handleUnreadRefresh);
    return () => window.removeEventListener(UNREAD_REFRESH_EVENT, handleUnreadRefresh);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleReset = () => {
      setProfileMenuOpen(false);
      setAccountSidebarOpen(false);
    };
    window.addEventListener(AUTH_UI_RESET_EVENT, handleReset);
    return () => window.removeEventListener(AUTH_UI_RESET_EVENT, handleReset);
  }, []);

  useEffect(() => {
    if (!sidebarFeatureEnabled || typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 1024px)");
    const handleChange = () => {
      const matches = media.matches;
      setUseSidebarDesktop(matches);
      if (matches) {
        setProfileMenuOpen(false);
      } else {
        setAccountSidebarOpen(false);
      }
    };
    handleChange();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, [sidebarFeatureEnabled]);

  const applyMobileLocationSuggestion = (suggestion) => {
    if (!suggestion) return;
    setLocation(normalizeSelectedLocation(suggestion));
    router.refresh();
    setMobileLocationInput("");
    setMobileLocationSuggestions([]);
    setMobileLocationSuggestIndex(-1);
    setMobileLocationSelectHint(null);
    setMobileLocationQueryEdited(false);
    setMobileLocationOpen(false);
  };

  useEffect(() => {
    if (!authDiagEnabled) return;
    console.log("[AUTH_DIAG] cta:HeaderAccountWidget", {
      providerInstanceId,
      authStatus,
      hasAuth,
      authBusy,
      authAction,
      authAttemptId,
      lastAuthEvent,
      disableCtas,
    });
  });

  useEffect(() => {
    if (!authDiagEnabled) return undefined;
    if (typeof window === "undefined") return undefined;

    const describeNode = (node) => {
      if (!node || !node.tagName) return null;
      const id = node.id ? `#${node.id}` : "";
      const className =
        typeof node.className === "string" && node.className.trim()
          ? `.${node.className.trim().split(/\s+/).slice(0, 3).join(".")}`
          : "";
      return `${node.tagName.toLowerCase()}${id}${className}`;
    };

    const logStyleChain = (el, label) => {
      const chain = [];
      let current = el;
      let depth = 0;
      while (current && depth < 7) {
        const style = window.getComputedStyle(current);
        chain.push({
          label: depth === 0 ? label : `parent-${depth}`,
          node: describeNode(current),
          pointerEvents: style.pointerEvents,
          opacity: style.opacity,
          position: style.position,
          zIndex: style.zIndex,
        });
        if (current.tagName?.toLowerCase() === "body") break;
        current = current.parentElement;
        depth += 1;
      }
      return chain;
    };

    const login = document.querySelector("[data-public-cta='signin']");
    const signup = document.querySelector("[data-public-cta='signup']");
    const modalDialog = document.querySelector("[aria-modal='true']");
    const drawerHost = document.querySelector("div[data-mobile-sidebar-drawer='1']");
    const overlayPresent = Boolean(modalDialog || drawerHost);

    const diagDisableReasons = [
      authBusy ? "authBusy" : null,
      loading && !hasAuth ? "authStatus=loading" : null,
      profileMenuOpen ? "profileMenuOpen" : null,
      overlayPresent ? "overlayPresent" : null,
      login?.disabled ? "signin.disabled" : null,
      login?.getAttribute?.("aria-disabled") ? "signin.aria-disabled" : null,
      signup?.disabled ? "signup.disabled" : null,
      signup?.getAttribute?.("aria-disabled") ? "signup.aria-disabled" : null,
    ].filter(Boolean);

    console.log("[AUTH_DIAG] cta:HeaderAccountWidget:render", {
      providerInstanceId,
      authStatus,
      hasAuth,
      authBusy,
      authAction,
      authAttemptId,
      lastAuthEvent,
      disableCtas,
      diagDisableReasons,
      loginStyle: login ? logStyleChain(login, "signin") : null,
      signupStyle: signup ? logStyleChain(signup, "signup") : null,
      overlayPresent,
      overlayNodes: {
        modalDialog: modalDialog ? describeNode(modalDialog) : null,
        drawerHost: drawerHost ? describeNode(drawerHost) : null,
      },
    });
  });

  const desktopSkeleton = (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-2xl bg-white/10 border border-white/10" />
      <div className="h-4 w-20 rounded bg-white/10" />
    </div>
  );

  const scheduleProfileMenuClose = useCallback(() => {
    if (!profileMenuOpen) return;
    if (typeof queueMicrotask === "function") {
      queueMicrotask(() => setProfileMenuOpen(false));
    } else {
      setTimeout(() => setProfileMenuOpen(false), 0);
    }
  }, [profileMenuOpen]);

  const handleAccountNavigate = useCallback(() => {
    if (useSidebarDesktop) {
      setAccountSidebarOpen(false);
      return;
    }
    scheduleProfileMenuClose();
  }, [useSidebarDesktop, scheduleProfileMenuClose]);

  if (variant === "desktop") {
    if (showRateLimit) {
      return (
        <div className="text-sm text-white/70" aria-live="polite">
          {rateLimitMessage || "Temporarily rate-limited. Please wait a moment."}
        </div>
      );
    }
    if (loading && !hasAuth) return desktopSkeleton;

    if (!hasAuth) {
      return (
        <>
          <button
            type="button"
            onClick={() => openModal("customer-login")}
            disabled={disableCtas}
            aria-busy={disableCtas}
            className={`inline-flex h-11 items-center rounded-lg px-2.5 text-sm font-medium text-gray-300 transition-colors duration-200 ease-out hover:text-purple-400 ${
              disableCtas ? "opacity-60 cursor-not-allowed" : ""
            }`}
            data-public-cta="signin"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => openModal("customer-signup")}
            disabled={disableCtas}
            aria-busy={disableCtas}
            className={`inline-flex h-11 items-center rounded-xl border border-purple-300/20 bg-[linear-gradient(135deg,rgba(124,58,237,0.92),rgba(147,51,234,0.92))] px-5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(124,58,237,0.18)] transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-[linear-gradient(135deg,rgba(124,58,237,1),rgba(168,85,247,0.98))] hover:shadow-[0_10px_22px_rgba(124,58,237,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300/20 active:translate-y-0 ${
              disableCtas ? "opacity-60 cursor-not-allowed" : ""
            }`}
            data-public-cta="signup"
          >
            Sign up
          </button>
        </>
      );
    }

    return (
      <>
        <div className="flex items-center gap-3">
        {hasHydrated && isBusiness ? (
          <Link
            href="/go/dashboard"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white/90 border border-white/20 hover:bg-white/10 transition"
          >
            Dashboard
          </Link>
        ) : null}
        <div className="relative" ref={dropdownRef} data-nav-guard="1">
          <button
            ref={accountTriggerRef}
            onClick={() => {
              if (useSidebarDesktop) {
                setAccountSidebarOpen(true);
                return;
              }
              setProfileMenuOpen((open) => !open);
            }}
            className="flex items-center gap-3 rounded-2xl bg-white/5 px-3 py-1.5 border border-white/10 hover:border-white/30 transition"
            data-nav-guard="1"
            aria-haspopup="dialog"
            aria-expanded={useSidebarDesktop ? accountSidebarOpen : profileMenuOpen}
          >
            <span className="relative">
              <SafeAvatar
                src={avatar}
                userMetadata={accountUser?.user_metadata || user?.user_metadata}
                name={displayName}
                displayName={displayName}
                businessName={accountBusiness?.business_name}
                email={email}
                shape={isBusiness ? "rounded-square" : "circle"}
                identityType={isBusiness ? "business" : "person"}
                alt="Profile avatar"
                fallbackSrc={isBusiness ? "/business-placeholder.png" : CUSTOMER_AVATAR_FALLBACK}
                className="h-[42px] w-[42px] bg-gray-200 object-cover border border-gray-100 shadow-sm ring-1 ring-gray-300"
                initialsClassName="text-xs"
                width={40}
                height={40}
              />
              {unreadCount > 0 ? (
                <span className="absolute -bottom-1 -left-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold !text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </span>
            <span className="hidden sm:block text-sm font-semibold text-white/90 max-w-[120px] truncate">
              {displayName}
            </span>
            <ChevronDown className="h-4 w-4 text-white/70" />
          </button>

          {profileMenuOpen ? (
            <div
              className="absolute right-0 mt-4 w-80 rounded-3xl px-1.5 pb-3 pt-1.5 yb-dropdown-surface z-[5100]"
              data-nav-guard="1"
            >
              <div className="rounded-[26px]">
                <div className="flex items-center gap-3 px-4 py-4">
                  <SafeAvatar
                    src={avatar}
                    userMetadata={accountUser?.user_metadata || user?.user_metadata}
                    name={displayName}
                    displayName={displayName}
                    businessName={accountBusiness?.business_name}
                    email={email}
                    shape={isBusiness ? "rounded-square" : "circle"}
                    identityType={isBusiness ? "business" : "person"}
                    alt="Profile avatar"
                    fallbackSrc={isBusiness ? "/business-placeholder.png" : CUSTOMER_AVATAR_FALLBACK}
                    className="h-[50px] w-[50px] bg-gray-200 object-cover border border-gray-100 shadow-sm ring-1 ring-gray-300"
                    initialsClassName="text-[13px]"
                    width={48}
                    height={48}
                  />
                  <div>
                    {supportModeActive ? (
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                        Viewing as
                      </p>
                    ) : null}
                    <p className="text-sm font-semibold">{displayName}</p>
                    {email ? <p className="text-xs yb-dropdown-muted">{email}</p> : null}
                  </div>
                </div>

                <AccountMenuItems
                  variant="dropdown"
                  isCustomer={isCustomer}
                  isBusiness={isBusiness}
                  hasBusinessAccess={hasBusinessAccess}
                  hasAdminMembership={hasAdminMembership}
                  businessAccessContexts={businessAccessContexts}
                  businessName={accountBusiness?.business_name}
                  unreadCount={unreadCount}
                  supportUnreadCount={supportUnreadCount}
                  onNavigate={handleAccountNavigate}
                  logout={(
                    <LogoutButton
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                      onSuccess={() => setProfileMenuOpen(false)}
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </LogoutButton>
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
        </div>
        {hasAuth && useSidebarDesktop ? (
          <AccountSidebar
            open={accountSidebarOpen}
            onOpenChange={setAccountSidebarOpen}
            anchorRef={accountTriggerRef}
            premiumCustomer={isCustomer}
            premiumBusiness={isBusiness}
            avatarShape={isBusiness ? "rounded-square" : "circle"}
            displayName={displayName}
            businessName={accountBusiness?.business_name}
            email={email}
            avatar={avatar}
            userMetadata={accountUser?.user_metadata || user?.user_metadata}
            scrollLockMode={isCustomer ? "stable-gutter" : "fixed"}
            backgroundRootSelector={
              isCustomer
                ? '[data-testid="customer-shell-content"], [data-app-shell-main="1"]'
                : undefined
            }
          >
            <AccountMenuItems
              variant="sidebar"
              isCustomer={isCustomer}
              isBusiness={isBusiness}
              hasBusinessAccess={hasBusinessAccess}
              hasAdminMembership={hasAdminMembership}
              businessAccessContexts={businessAccessContexts}
              businessName={accountBusiness?.business_name}
              unreadCount={unreadCount}
              supportUnreadCount={supportUnreadCount}
              onNavigate={handleAccountNavigate}
              logout={(
                <LogoutButton
                  className={
                    isCustomer || isBusiness
                      ? "flex w-full items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
                      : "flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                  }
                  onSuccess={() => setAccountSidebarOpen(false)}
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </LogoutButton>
              )}
            />
          </AccountSidebar>
        ) : null}
      </>
    );
  }

  return (
    <MobileSidebarDrawer
      open={mobileMenuOpen}
      onClose={() => onCloseMobileMenu?.()}
      title={
        isMobileBusinessAccount ? (
          "Your Account"
        ) : hasAuth ? (
          "My account"
        ) : (
          <>
            <span className="block w-[178px] max-w-[calc(100%-3.5rem)]">
              <YourBarrioLogo
                variant="wordmark-light"
                alt="YourBarrio"
                width={178}
                height={68}
                className="h-auto w-full select-none"
                draggable={false}
              />
            </span>
            <span className="block text-[28px] leading-8 tracking-[-0.02em]">
              Discover local shops near you
            </span>
          </>
        )
      }
      subtitle={
        hasAuth
          ? "YourBarrio"
          : <span className="text-base leading-6">Shop nearby. Pick up locally.</span>
      }
      id={mobileDrawerId}
      showHeader={!hasAuth || isMobileBusinessAccount}
      closeButtonVariant={!hasAuth ? "soft" : "default"}
      widthClassName={isMobileBusinessAccount ? "w-[90vw] max-w-[390px]" : undefined}
    >
      <div
        className={`flex min-h-full flex-col ${
          isMobileBusinessAccount ? "gap-4" : hasAuth ? "gap-6" : "gap-5"
        }`}
        data-mobile-business-account={isMobileBusinessAccount ? "1" : undefined}
        data-guest-mobile-sidebar={!hasAuth ? "1" : undefined}
        data-nav-surface={surface}
        data-nav-guard="1"
      >
        {hasAuth ? (
          <div
            className={
              isMobileBusinessAccount
                ? "flex min-w-0 items-center gap-3 px-1 py-1"
                : "flex items-center gap-3 rounded-2xl border border-[var(--yb-border)] bg-white px-4 py-3"
            }
            data-testid={isMobileBusinessAccount ? "mobile-business-profile" : undefined}
          >
            <SafeAvatar
              src={avatar}
              userMetadata={accountUser?.user_metadata || user?.user_metadata}
              name={displayName}
              displayName={displayName}
              businessName={accountBusiness?.business_name}
              email={email}
              shape={isBusiness ? "rounded-square" : "circle"}
              identityType={isBusiness ? "business" : "person"}
              alt="Profile avatar"
              fallbackSrc={isBusiness ? "/business-placeholder.png" : CUSTOMER_AVATAR_FALLBACK}
              className={`bg-gray-200 object-cover border border-gray-100 shadow-sm ring-1 ring-gray-300 ${
                isMobileBusinessAccount ? "h-12 w-12" : "h-[46px] w-[46px]"
              }`}
              initialsClassName="text-[13px]"
              width={isMobileBusinessAccount ? 48 : 44}
              height={isMobileBusinessAccount ? 48 : 44}
            />
            <div className="min-w-0">
              {supportModeActive ? (
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                  Viewing as
                </p>
              ) : null}
              <p className="truncate text-[15px] font-semibold text-gray-900">{displayName}</p>
              {email ? (
                <p className="truncate text-[13px] text-gray-500 yb-dropdown-muted">{email}</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {showRateLimit ? (
          <div className="text-sm text-white/70" aria-live="polite">
            {rateLimitMessage || "Temporarily rate-limited. Please wait a moment."}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className={hasAuth ? "flex items-center gap-3" : "flex flex-col gap-2"}>
            <button
              type="button"
              onClick={() => setMobileLocationOpen((open) => !open)}
              className={`flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-[var(--yb-border)] bg-white text-left transition ${
                hasAuth
                  ? isMobileBusinessAccount
                    ? "min-h-16 px-3 py-2 hover:bg-black/5"
                    : "px-4 py-3 hover:bg-black/5"
                  : "px-3.5 py-2.5 shadow-sm hover:bg-slate-50"
              }`}
              aria-expanded={mobileLocationOpen}
              aria-controls="mobile-location-editor"
            >
              {hasAuth ? (
                <MapPin className="h-4 w-4" />
              ) : (
                <span className="yb-guest-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100">
                  <MapPin className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                {hasAuth ? (
                  <p className="text-xs uppercase tracking-[0.2em] yb-dropdown-muted">
                    Location
                  </p>
                ) : (
                  <p className="yb-guest-muted text-xs font-medium">Shopping in</p>
                )}
                <p className="text-sm font-semibold truncate">{locationLabel}</p>
                {!hasAuth ? (
                  <p className="yb-guest-muted mt-0.5 text-xs">Used to show nearby shops.</p>
                ) : null}
              </div>
              <ChevronDown
                className={`h-4 w-4 yb-dropdown-muted transition ${
                  mobileLocationOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {hasAuth ? (
              <CartNavActionClient variant="mobile" onNavigate={onCloseMobileMenu} />
            ) : (
              <CartNavActionClient variant="mobile-row" onNavigate={onCloseMobileMenu} />
            )}
          </div>

          {mobileLocationOpen ? (
            <div
              id="mobile-location-editor"
              className="rounded-2xl border border-[var(--yb-border)] bg-white px-4 py-4"
            >
              <div className="text-xs uppercase tracking-[0.22em] yb-dropdown-muted">
                Set location
              </div>
              <div className="mt-2 text-sm yb-dropdown-muted">Enter a city or ZIP code.</div>
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="text"
                  value={mobileLocationInput}
                  onChange={(event) => {
                    setMobileLocationInput(event.target.value);
                    setMobileLocationQueryEdited(true);
                    setMobileLocationSelectHint(null);
                    clearCurrentLocationError();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      setMobileLocationSuggestIndex((prev) =>
                        Math.min(prev + 1, mobileLocationSuggestions.length - 1)
                      );
                      return;
                    }
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      setMobileLocationSuggestIndex((prev) => Math.max(prev - 1, 0));
                      return;
                    }
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (mobileLocationSuggestions.length > 0) {
                        const selected =
                          mobileLocationSuggestIndex >= 0 &&
                          mobileLocationSuggestions[mobileLocationSuggestIndex]
                            ? mobileLocationSuggestions[mobileLocationSuggestIndex]
                            : mobileLocationSuggestions[0];
                        applyMobileLocationSuggestion(selected);
                        return;
                      }
                      setMobileLocationSelectHint("Select a suggestion to set your location.");
                    }
                  }}
                  placeholder="e.g. Austin, 78701"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={mobileLocationSuggestions.length > 0}
                  aria-controls="mobile-location-suggestions"
                  aria-activedescendant={
                    mobileLocationSuggestIndex >= 0
                      ? `mobile-location-suggestion-${mobileLocationSuggestIndex}`
                      : undefined
                  }
                  className="w-full rounded-lg border border-[var(--yb-border)] bg-white px-3 py-2 text-base placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--yb-focus)] focus:border-[var(--yb-focus)]"
                />
              </div>
              <button
                type="button"
                onClick={() => void requestCurrentLocation()}
                disabled={currentLocationLoading}
                className="yb-location-action mt-2 inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-semibold transition hover:bg-purple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/20 disabled:cursor-wait disabled:opacity-60"
                aria-busy={currentLocationLoading}
              >
                {currentLocationLoading ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <LocateFixed className="h-4 w-4" aria-hidden="true" />
                )}
                {currentLocationLoading ? "Finding your location…" : "Use my current location"}
              </button>
              {currentLocationError ? (
                <p className="yb-location-error mt-1 text-xs leading-5" role="status">
                  {currentLocationError}
                </p>
              ) : null}
              <div className="mt-3 text-xs yb-dropdown-muted min-h-[16px]">
                {mobileLocationSuggestLoading ? "Searching locations..." : ""}
              </div>
              {mobileLocationSuggestError ? (
                <div className="mt-3 text-xs text-rose-600">
                  {mobileLocationSuggestError}
                </div>
              ) : null}
              {mobileLocationSelectHint ? (
                <div className="mt-2 text-xs yb-dropdown-muted">
                  {mobileLocationSelectHint}
                </div>
              ) : null}
              {!mobileLocationSuggestLoading &&
              !mobileLocationSuggestError &&
              mobileLocationQueryEdited &&
              mobileLocationInput.trim().length >=
                LOCATION_SUGGESTION_MIN_QUERY_LENGTH &&
              mobileLocationSuggestions.length === 0 ? (
                <div className="mt-3 text-xs yb-dropdown-muted">
                  {mobileLocationNoMatchMessage}
                </div>
              ) : null}
              {mobileLocationQueryEdited && mobileLocationSuggestions.length > 0 ? (
                <div className="mt-3">
                  <div
                    id="mobile-location-suggestions"
                    role="listbox"
                    className="max-h-56 overflow-y-auto rounded-xl border border-[var(--yb-border)] bg-white p-1"
                  >
                  {mobileLocationSuggestions.map((suggestion, idx) => (
                    <button
                      key={suggestion.id || suggestion.label || idx}
                      id={`mobile-location-suggestion-${idx}`}
                      type="button"
                      role="option"
                      aria-selected={idx === mobileLocationSuggestIndex}
                      onClick={() => applyMobileLocationSuggestion(suggestion)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-black/5 rounded-lg ${
                        idx === mobileLocationSuggestIndex ? "bg-black/5" : ""
                      }`}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {!showRateLimit ? (
          !hasAuth ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    onCloseMobileMenu?.();
                    openModal("customer-signup");
                  }}
                  disabled={disableCtas}
                  aria-busy={disableCtas}
                  className={`rounded-xl border border-purple-300/20 bg-[var(--color-primary)] px-4 py-3 text-center text-sm font-semibold text-white shadow-[0_8px_18px_rgba(110,52,255,0.16)] transition duration-200 ease-out hover:bg-[var(--color-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300/20 ${
                    disableCtas ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  data-public-cta="signup"
                >
                  Create free account
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onCloseMobileMenu?.();
                    openModal("customer-login");
                  }}
                  disabled={disableCtas}
                  aria-busy={disableCtas}
                  className={`yb-guest-signin rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold shadow-sm transition hover:bg-slate-50 ${
                    disableCtas ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  data-public-cta="signin"
                >
                  Sign in
                </button>
              </div>

              <div className="px-1 pt-1">
                <p className="yb-guest-muted text-xs font-semibold uppercase tracking-[0.16em]">
                  Why join?
                </p>
                <div className="mt-3 flex flex-col gap-2.5">
                  {["Save local finds", "Check out faster", "Track orders & pickups"].map(
                    (item) => (
                      <div key={item} className="yb-guest-muted flex items-center gap-2 text-sm">
                        <CheckCircle2 className="yb-guest-check h-4 w-4 shrink-0" />
                        <span>{item}</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="border-t border-[var(--yb-border)] px-1 pt-4">
                <p className="yb-guest-muted text-xs font-medium">Own a local business?</p>
                <Link
                  href="/business-auth/register"
                  prefetch={false}
                  onClick={() => onCloseMobileMenu?.()}
                  className="mt-1 inline-flex text-sm font-semibold text-[var(--color-primary)] transition hover:text-[var(--color-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/20"
                  data-safe-nav="1"
                >
                  Sell on YourBarrio →
                </Link>
              </div>
            </div>
          ) : (
            <>
              {isBusiness ? (
                <AccountMenuItems
                  variant="sidebar"
                  isCustomer={false}
                  isBusiness
                  hasBusinessAccess
                  businessAccessContexts={businessAccessContexts}
                  businessName={accountBusiness?.business_name}
                  compact
                  onNavigate={() => onCloseMobileMenu?.()}
                  logout={(
                    <LogoutButton
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
                      onSuccess={() => onCloseMobileMenu?.()}
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </LogoutButton>
                  )}
                />
              ) : null}

              {isCustomer ? (
                <AccountMenuItems
                  variant="sidebar"
                  isCustomer={isCustomer}
                  isBusiness={false}
                  hasBusinessAccess={hasBusinessAccess}
                  hasAdminMembership={hasAdminMembership}
                  businessAccessContexts={businessAccessContexts}
                  businessName={accountBusiness?.business_name}
                  unreadCount={unreadCount}
                  supportUnreadCount={supportUnreadCount}
                  onNavigate={() => onCloseMobileMenu?.()}
                  logout={(
                    <LogoutButton mobile onSuccess={() => onCloseMobileMenu?.()} />
                  )}
                />
              ) : null}
            </>
          )
        ) : null}

        {hasAuth && !isCustomer && !isBusiness ? (
          <LogoutButton mobile onSuccess={() => onCloseMobileMenu?.()} />
        ) : null}

        {!hasAuth && !showRateLimit ? (
          <nav
            aria-label="Guest menu footer"
            className="yb-guest-muted mt-auto flex items-center gap-2 px-1 pt-4 text-xs"
          >
            <a
              href="mailto:support@yourbarrio.com"
              className="transition hover:text-[var(--yb-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/20"
            >
              Help
            </a>
            <span aria-hidden="true">·</span>
            <Link
              href="/terms"
              onClick={() => onCloseMobileMenu?.()}
              className="transition hover:text-[var(--yb-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/20"
              data-safe-nav="1"
            >
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <Link
              href="/privacy"
              onClick={() => onCloseMobileMenu?.()}
              className="transition hover:text-[var(--yb-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/20"
              data-safe-nav="1"
            >
              Privacy
            </Link>
          </nav>
        ) : null}
      </div>
    </MobileSidebarDrawer>
  );
}
